import React from 'react';
import { motion } from 'framer-motion';
import { FileTextIcon, VideoIcon, ListChecksIcon, MessageSquareIcon, CheckCircle2Icon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { SubpageHero } from '../../components/sections/subpages/SubpageHero';
import { PatternDecor } from '../../components/ui/PatternDecor';
const resources = [{
  icon: FileTextIcon,
  title: 'Practical guides',
  body: 'Step-by-step PDFs for common situations — school meetings, identity questions, sensitive conversations.',
  bg: 'bg-sage-100'
}, {
  icon: VideoIcon,
  title: 'Recorded expert talks',
  body: 'Short, focused sessions you can watch when you need them — no extra reading required.',
  bg: 'bg-[#EDF2F7]'
}, {
  icon: MessageSquareIcon,
  title: 'Family conversation tools',
  body: 'Scripts and prompts to help you have calm, honest conversations with your child about difficult topics.',
  bg: 'bg-[#F7EDE9]'
}, {
  icon: ListChecksIcon,
  title: 'Checklists & templates',
  body: 'Documentation templates, school communication checklists, and quick-reference cards for stressful moments.',
  bg: 'bg-sand-100'
}];
const principles = ['culturally sensitive', 'faith-rooted and non-political', 'easy to apply in daily life', 'safe for children and parents', 'focused on dignity and integration'];
export function ResourcesGuidance() {
  return <>
      <SubpageHero eyebrow="Practical Support for Everyday Parenting" title="Resources & Guidance" paragraphs={['Parents receive access to carefully curated resources designed for Muslim families living in non-Muslim environments.', 'Everything is created to be culturally sensitive, non-political, and easy to apply in daily life.']} primaryCta={{
      label: 'Explore the Resources',
      to: '#content'
    }} secondaryCta={{
      label: 'Preventing Discrimination',
      to: '/en/parents/preventing-discrimination'
    }} image="https://cdn.magicpatterns.com/uploads/cHe8qMcmyi3psE64ERz1VF/Resources-7.jpg" imageAlt="A tablet on a stand playing an IILH guidance video on a sunlit desk beside reading glasses and a cup of tea, with a city view through the window" compact masked data-id="element-4486" data-mp="peka7kerpy9o" />

      {/* What you get */}
      <section id="content" className="py-16 md:py-24 scroll-mt-24 bg-sage-50/60" data-id="element-4487" data-mp="hccpivltdrgj">
        <Container data-id="element-4488" data-mp="mbxxfviypa5e">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4489" data-mp="mamlh8fd4w37">
            What you get
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-4490" data-mp="r8xuxi1qx4kw">
            A library of practical materials you can use at home — to explain,
            prepare, and support your child in everyday situations.
          </p>
          <div className="mt-10 grid md:grid-cols-2 gap-5" data-id="element-4491" data-mp="3n88hb1b4mtve">
            {resources.map((r, i) => <motion.div key={r.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.06
          }} className={`rounded-2xl ${r.bg} border border-ink-900/5 p-6 flex gap-4`} data-id="element-4492" data-mp="3i9ou6xksrc1g" data-mpi={i}>
                <span className="w-11 h-11 inline-flex items-center justify-center rounded-full bg-white border border-forest-700/10 text-forest-700 flex-shrink-0" data-id="element-4493" data-mp="2pkznmj0z2eib">
                  <r.icon size={18} strokeWidth={1.6} data-id="element-4494" data-mp="srod3b1vrsd1" />
                </span>
                <div data-id="element-4495" data-mp="nuo68xx7ilkv">
                  <h3 className="font-display text-lg text-ink-900" data-id="element-4496" data-mp="nra51t0a4if0">
                    {r.title}
                  </h3>
                  <p className="mt-1 text-ink-700 text-sm leading-relaxed" data-id="element-4497" data-mp="10t5grx2iuriu">
                    {r.body}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </Container>
      </section>

      {/* All materials are created to be */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-white/60" data-id="element-4498" data-mp="qwsql966qvsu">
        <PatternDecor side="right" y="top" size="lg" data-id="element-4499" data-mp="d1hyg3jbsos6" />
        <Container data-id="element-4500" data-mp="hujeiev9qudc">
          <div className="grid lg:grid-cols-12 gap-12 items-center" data-id="element-4501" data-mp="hv2wtwotzkrw">
            <div className="lg:col-span-6" data-id="element-4502" data-mp="35x0cghdo8np">
              <h2 className="font-display text-3xl md:text-4xl text-ink-900 leading-tight" data-id="element-4503" data-mp="3784iheqjifc">
                Built to reduce stress, not add to it
              </h2>
              <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-4504" data-mp="3qb5wbf5si355">
                Every resource is short, practical, and written for busy
                parents. No jargon. No politics. No pressure. Just clear,
                applicable guidance you can use today.
              </p>
              <p className="mt-4 text-ink-700 leading-relaxed" data-id="element-4505" data-mp="36ouit1nvy5ow">
                Everything is designed to help you respond with confidence —
                without escalation or confrontation.
              </p>
            </div>
            <div className="lg:col-span-6" data-id="element-4506" data-mp="3fo7ywd6ilsil">
              <div className="rounded-2xl bg-sage-50 border border-sage-200 p-7 md:p-8 shadow-soft" data-id="element-4507" data-mp="3uma2uohgkg0d">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-4" data-id="element-4508" data-mp="3pn6uau29rrnn">
                  All materials are created to be
                </p>
                <ul className="space-y-3 text-ink-700" data-id="element-4509" data-mp="7bactcotonl8">
                  {principles.map(p => <li key={p} className="flex items-center gap-3" data-id="element-4510" data-mp="39p3g1bhyrfs">
                      <CheckCircle2Icon size={16} className="text-forest-700 flex-shrink-0" strokeWidth={1.6} data-id="element-4511" data-mp="h85chywu0u13" />
                      <span data-id="element-4512" data-mp="361trg48msvze">{p}</span>
                    </li>)}
                </ul>
                <p className="mt-6 pt-5 border-t border-sage-200 text-sm text-ink-500 leading-relaxed" data-id="element-4513" data-mp="3a2djitecl9eo">
                  We provide education and guidance. We do not provide legal
                  representation.
                </p>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-[#F7EDE9]/60" data-id="element-4514" data-mp="3tptdwrywu086">
        <Container data-id="element-4515" data-mp="3toa15bcgq7b5">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8" data-id="element-4516" data-mp="3tro1ke1kg6fy">
            <div className="max-w-xl" data-id="element-4517" data-mp="1jxx0jcrxqhi">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4518" data-mp="6lna04jk4e88">
                Get the resources you need
              </h2>
              <p className="mt-4 text-ink-700" data-id="element-4519" data-mp="23fi3mwk1but4">
                Membership unlocks the full library plus monthly expert sessions
                and direct support.
              </p>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-4520" data-mp="20v28ixyn3r5a">
              <Link to="/en/waitlist" data-id="element-4521" data-mp="1w1zb2eqqrxjt">
                <Button variant="primary" data-id="element-4522" data-mp="1w0y1yftadl4q">Join Membership</Button>
              </Link>
              <Link to="/en/contact" data-id="element-4523" data-mp="1r3pfw450owy7">
                <Button variant="secondary" data-id="element-4524" data-mp="1m4347z4u7ori">Contact Us</Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </>;
}